import React, { useMemo, useState, useEffect, useCallback } from "react";
import { useStudents } from "@/contexts";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Printer } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { differenceInDays, format } from "date-fns";
import { Badge } from "@/components/ui/badge";
import { 
  printProgramsListPaginatedAsJPG, 
  printProgramsListPaginatedAsPDF,
  printIndividualProgramAsJPG,
  printIndividualProgramAsPDF
} from "@/utils/printing/programListPaginatedPrinter";
import ProgramsPageSelectionDialog from "@/components/programs/ProgramsPageSelectionDialog";
import ProgramsPagination from "@/components/programs/ProgramsPagination";
import ProgramFormatDialog from "@/components/programs/ProgramFormatDialog";
import { toast } from "sonner";

const PROGRAMS_PER_PAGE = 18; // Updated to match printer settings

const ProgramsListPage = () => {
  const { students, loading } = useStudents();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");
  const [focusedRowIndex, setFocusedRowIndex] = useState(-1);
  const [isPrinting, setIsPrinting] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [isPageDialogOpen, setIsPageDialogOpen] = useState(false);
  const [isFormatDialogOpen, setIsFormatDialogOpen] = useState(false);
  const [isIndividualFormatDialogOpen, setIsIndividualFormatDialogOpen] = useState(false);
  const [selectedProgram, setSelectedProgram] = useState<any>(null);

  const programsList = useMemo(() => {
    const programs: any[] = [];
    students.forEach(student => {
      student.programs.forEach(program => {
        programs.push({
          ...program,
          student: student.name,
          remainingDays: program.completionDate ? differenceInDays(new Date(program.completionDate), new Date()) : null,
          progress: calculateProgress(new Date(program.enrolledAt), program.completionDate ? new Date(program.completionDate) : undefined),
          enrolledAt: format(new Date(program.enrolledAt), 'MMM dd, yyyy'),
          completionDate: program.completionDate ? format(new Date(program.completionDate), 'MMM dd, yyyy') : 'Not set',
          status: 'Active'
        });
      });
    });
    return programs.sort((a, b) => new Date(b.enrolledAt).getTime() - new Date(a.enrolledAt).getTime());
  }, [students]);

  function calculateProgress(startDate: Date, endDate: Date | undefined): number {
    if (!endDate) return 0;
    const total = differenceInDays(endDate, startDate);
    const elapsed = differenceInDays(new Date(), startDate);
    return Math.min(Math.max(Math.round(elapsed / total * 100), 0), 100);
  }

  const filteredPrograms = programsList.filter(program => 
    program.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    program.category.toLowerCase().includes(searchQuery.toLowerCase()) ||
    program.student.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Calculate pagination
  const totalPrograms = filteredPrograms.length;
  const totalPages = Math.ceil(totalPrograms / PROGRAMS_PER_PAGE);
  const startIndex = (currentPage - 1) * PROGRAMS_PER_PAGE;
  const endIndex = startIndex + PROGRAMS_PER_PAGE;

  // Get paginated data for table display
  const paginatedPrograms = filteredPrograms.slice(startIndex, endIndex);

  const handlePrintProgramsList = () => {
    if (totalPrograms === 0) {
      toast.error("No programs available to download");
      return;
    }
    
    console.log('Print catalog clicked, total programs:', totalPrograms);
    
    const totalPagesForPrint = Math.ceil(totalPrograms / 18); // Use printer's PROGRAMS_PER_PAGE
    console.log('Total pages for print:', totalPagesForPrint);
    
    if (totalPagesForPrint === 1) {
      console.log('Single page, opening format dialog');
      setIsFormatDialogOpen(true);
    } else {
      console.log('Multiple pages, opening page selection dialog');
      setIsPageDialogOpen(true);
    }
  };

  const handleDownloadPages = async (format: 'jpg' | 'pdf', startPage: number, endPage: number) => {
    if (isPrinting) return;
    
    console.log('Downloading pages:', { format, startPage, endPage });
    setIsPrinting(true);
    
    try {
      if (format === 'jpg') {
        await printProgramsListPaginatedAsJPG(filteredPrograms, startPage, endPage);
      } else {
        await printProgramsListPaginatedAsPDF(filteredPrograms, startPage, endPage);
      }
    } catch (error) {
      console.error('Error downloading programs list:', error);
      toast.error("Failed to download programs list");
    } finally {
      setIsPrinting(false);
    }
  };

  const handleDirectFormatDownload = async (format: 'jpg' | 'pdf') => {
    const totalPagesForPrint = Math.ceil(totalPrograms / 18);
    console.log('Direct format download:', { format, totalPagesForPrint });
    await handleDownloadPages(format, 1, totalPagesForPrint);
  };

  const handlePrintIndividualProgram = (program: any) => {
    console.log('Individual program print clicked:', program.title);
    setSelectedProgram(program);
    setIsIndividualFormatDialogOpen(true);
  };

  const handleIndividualProgramDownload = async (format: 'jpg' | 'pdf') => {
    if (!selectedProgram) return;
    
    console.log('Individual program download:', { format, program: selectedProgram.title });
    
    try {
      if (format === 'jpg') {
        await printIndividualProgramAsJPG(selectedProgram);
      } else {
        await printIndividualProgramAsPDF(selectedProgram);
      }
    } catch (error) {
      console.error('Error printing individual program:', error);
      toast.error('Failed to print program details');
    }
  };

  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (e.key === 'Escape') {
      if (searchQuery) {
        setSearchQuery('');
      } else {
        navigate('/');
      }
    }

    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      setFocusedRowIndex(prev => {
        const totalRows = paginatedPrograms.length;
        const newIndex = e.key === 'ArrowDown' 
          ? Math.min(prev + 1, totalRows - 1)
          : Math.max(prev - 1, 0);
        return newIndex;
      });
    }
  }, [searchQuery, navigate, paginatedPrograms]);

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  // Reset to first page when search changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery]);

  const handleSearchFocus = useCallback((e: React.FocusEvent<HTMLInputElement>) => {
    if (window.innerWidth <= 768) {
      setTimeout(() => {
        e.target.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 100);
    }
  }, []);

  if (loading) {
    return <div>Loading...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-3xl font-bold tracking-tight">Programs List</h2>
        <div className="flex items-center gap-4">
          <Button 
            onClick={() => navigate('/')}
            variant="outline"
            size="sm"
            aria-label="Back to Dashboard"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Dashboard
          </Button>
          <Button 
            onClick={handlePrintProgramsList}
            variant="outline"
            size="sm"
            className="flex items-center gap-2"
            disabled={isPrinting || totalPrograms === 0}
          >
            <Printer className="h-4 w-4" />
            {isPrinting ? 'Processing...' : 'Print Catalog'}
          </Button>
        </div>
      </div>

      <div className="bg-white dark:bg-slate-800 rounded-lg shadow">
        <div className="p-4">
          <Input
            type="search"
            placeholder="Search programs by title, category or student name... (Press '/' to focus)"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="max-w-xl"
            onFocus={handleSearchFocus}
            aria-label="Search programs"
          />
        </div>

        <div className="overflow-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Program Title</TableHead>
                <TableHead>Category</TableHead>
                <TableHead>Student</TableHead>
                <TableHead>Enrolled</TableHead>
                <TableHead>Completion Date</TableHead>
                <TableHead>Remaining</TableHead>
                <TableHead>Progress</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {paginatedPrograms.map((program, index) => (
                <TableRow 
                  key={`${program.title}-${index}`}
                  className={focusedRowIndex === index ? 'bg-muted' : ''}
                  tabIndex={0}
                  role="row"
                  aria-selected={focusedRowIndex === index}
                >
                  <TableCell className="font-medium">{program.title}</TableCell>
                  <TableCell>{program.category}</TableCell>
                  <TableCell>{program.student}</TableCell>
                  <TableCell>{program.enrolledAt}</TableCell>
                  <TableCell>{program.completionDate}</TableCell>
                  <TableCell>
                    {program.remainingDays !== null ? `${program.remainingDays} days` : 'N/A'}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <div className="h-2 w-24 bg-gray-200 rounded-full overflow-hidden">
                        <div className="h-full bg-blue-500" style={{
                          width: `${program.progress}%`
                        }} />
                      </div>
                      <span className="text-sm text-muted-foreground">
                        {program.progress}%
                      </span>
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge variant={program.status === 'Active' ? 'default' : 'secondary'}>
                      {program.status}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={() => handlePrintIndividualProgram(program)}
                      aria-label={`Print details for ${program.title}`}
                    >
                      <Printer className="h-4 w-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="p-4 border-t">
            <ProgramsPagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={setCurrentPage}
            />
          </div>
        )}

        {/* Page info */}
        <div className="p-4 text-sm text-muted-foreground text-center border-t">
          Showing {startIndex + 1} to {Math.min(endIndex, totalPrograms)} of {totalPrograms} programs
        </div>
      </div>

      {/* Page Selection Dialog */}
      <ProgramsPageSelectionDialog
        isOpen={isPageDialogOpen}
        onClose={() => setIsPageDialogOpen(false)}
        totalPrograms={totalPrograms}
        onDownload={handleDownloadPages}
      />

      {/* Format Selection Dialog for Print Catalog */}
      <ProgramFormatDialog
        isOpen={isFormatDialogOpen}
        onClose={() => setIsFormatDialogOpen(false)}
        onDownload={handleDirectFormatDownload}
        title="Download Programs Catalog"
        description={`Choose format for downloading the complete programs catalog (${totalPrograms} program${totalPrograms !== 1 ? 's' : ''})`}
      />

      {/* Format Selection Dialog for Individual Program */}
      <ProgramFormatDialog
        isOpen={isIndividualFormatDialogOpen}
        onClose={() => setIsIndividualFormatDialogOpen(false)}
        onDownload={handleIndividualProgramDownload}
        title="Download Program Details"
        description={`Choose format for downloading "${selectedProgram?.title || 'program'}" details`}
      />
    </div>
  );
};

export default ProgramsListPage;
